import makeStyles from '@mui/styles/makeStyles';
import { greys, mainColors } from '../../../../../styling/theme';
import { Dispatch, FC, useEffect, useState } from 'react';
import CachedIcon from '@mui/icons-material/Cached';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
} from '../../../../../utilities/numberFormatters';
import Button from '@mui/material/Button';
import { Tooltip } from '@mui/material';
import { KidsData } from '../../kids/editor/KidsEditor';
import { useRaidrKids } from '../../kids/context/RaidrKidsContext';
import { useKIDsFigureData } from '../../kids/services/queries';

const useStyles = makeStyles(() => ({
  conrolContainer: {
    display: 'flex',
  },
  toggleIcon: {
    color: mainColors.mainBlue,
  },
  toggleIconMessage: {
    transform: 'translateY(5.00rem)',
  },
  arrow: {
    '&:before': {
      border: '1px solid #8e8e8e',
    },
    color: greys.grey600,
  },
  tooltip: {
    backgroundColor: greys.grey600,
    border: '1px solid #8e8e8e',
    color: 'white',
  },
}));

interface RefreshFiguresButtonProps {
  setPriipsKidsContent: Dispatch<any>;
  clientId: string;
  fundId: string;
  shareClass: string;
  documentLanguage: string | null;
}

const RefreshFiguresButton: FC<RefreshFiguresButtonProps> = (props) => {
  const { setFiguresUpdated } = useRaidrKids();
  const {
    clientId,
    fundId,
    shareClass,
    documentLanguage,
    setPriipsKidsContent,
  } = props;
  const classes = useStyles();

  const { data: kidFigures, isFetching } = useKIDsFigureData(
    clientId,
    fundId,
    shareClass,
  );

  // Create a variable for indicating if the current figures and published figures are the same
  // If this is the case we do not allow the user to refresh the figures.
  const [figuresMatch, setFiguresMatch] = useState<boolean>(false);

  // Function for searching through the current data and updating the relavent fields with the updated figures.
  // Currently this is a little messy as the data returned from the back end is not great.
  function updateFigures() {
    setPriipsKidsContent((allContent: KidsData[]) => {
      // Create a copy of the data
      const newContent = [...allContent];
      // First update the sri value
      // Find the srri_chart tag in the data and update the content
      let sri_index = -1;
      let performance_scenarios_index = -1;
      let costs_over_time_index = -1;
      let composition_of_costs_index = -1;
      for (let i = 0; i < newContent.length; i++) {
        if (
          sri_index !== -1 &&
          performance_scenarios_index !== -1 &&
          costs_over_time_index !== -1 &&
          composition_of_costs_index !== -1
        ) {
          break;
        }
        if (newContent[i].tag === 'srri_chart') {
          sri_index = i;
        } else if (newContent[i].tag === 'table_style4') {
          performance_scenarios_index = i;
        } else if (newContent[i].tag === 'table_style5') {
          costs_over_time_index = i;
        } else if (newContent[i].tag === 'table_style6') {
          composition_of_costs_index = i;
        }
      }

      // Get the current figure data once
      const figuresData = kidFigures.current_data[0];

      // Update the content for each section if found
      if (sri_index !== -1) {
        newContent[sri_index].content = JSON.stringify(figuresData);
      }

      if (performance_scenarios_index !== -1) {
        newContent[performance_scenarios_index].content =
          JSON.stringify(figuresData);
      }

      if (costs_over_time_index !== -1) {
        newContent[costs_over_time_index].content = JSON.stringify(figuresData);
      }

      if (composition_of_costs_index !== -1) {
        newContent[composition_of_costs_index].content =
          JSON.stringify(figuresData);
      }

      return newContent;
    });

    setFiguresUpdated(true);
    setFiguresMatch(true);
  }

  // Create a messgae for the tooltip to explain  its use or why it is disabled
  const [tooltipMessage, setTooltipMessage] = useState<string>(
    'Update figures within document',
  );

  useEffect(() => {
    if (kidFigures) {
      // If current and published are the same then indicate that the figures match
      if (!kidFigures.current_data.length) {
        // We will us the figures match variable here to diasble the button
        setFiguresMatch(true);
        // Update the tooltip message
        setTooltipMessage('No figures available to update');
      } else if (
        kidFigures.current_data[0].calculation_date ===
        kidFigures.published_data[0]?.calculation_date
      ) {
        setFiguresMatch(true);
        // Update the tooltip message
        setTooltipMessage(
          'Latest Figures are already contained in the document',
        );
      }
    }
  }, [kidFigures]);

  return (
    <div className={classes.conrolContainer}>
      <Tooltip
        classes={{ arrow: classes.arrow, tooltip: classes.tooltip }}
        title={tooltipMessage}
        placement="top"
        arrow
        enterNextDelay={2000}
        enterDelay={2000}
      >
        <Button
          variant="contained"
          sx={{
            fontSize: '0.94rem',
            width: '11.25rem',
            color: 'white',
            backgroundColor: mainColors.mainBlue,
            borderRadius: '0.12rem',
            padding: '0.12rem',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor:
                figuresMatch || documentLanguage === 'Swiss English'
                  ? greys.grey400
                  : mainColors.mainBlue_lighter,
            },
            fontWeight: 600,
            textTransform: 'none',
            '&.Mui-disabled': {
              backgroundColor: greys.grey400,
              cursor: 'not-allowed',
            },
          }}
          disableElevation={true}
          disabled={
            figuresMatch || isFetching || documentLanguage === 'Swiss English'
          }
          onClick={updateFigures}
          endIcon={
            <CachedIcon
              sx={{
                padding: '0.12rem',
                fontSize: '1.88rem',
                width: '1.88rem',
                height: '1.56rem',
                marginRight: '0.31rem',
                animation: isFetching ? 'spin 2s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                },
              }}
            />
          }
        >
          Refresh Figures
        </Button>
      </Tooltip>
    </div>
  );
};

export default RefreshFiguresButton;
